import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

export interface BoardMember {
    id: string;
    name: string;
    role: string;
    description: string;
    tags: string[];
    image: string;
    isHost: boolean;
}

export interface SupportMember {
    id: string;
    name: string;
    role: string;
    description: string;
    image: string;
    specialIcon?: string;
}

// Hook para buscar membros da diretoria
export const useBoardMembers = () => {
    return useQuery<BoardMember[]>({
        queryKey: ['board-members'],
        queryFn: async () => {
            const response = await api.get('/board');
            return response.data;
        }
    });
};

// Hook para buscar equipe de suporte
export const useSupportTeam = () => {
    return useQuery<SupportMember[]>({
        queryKey: ['support-team'],
        queryFn: async () => {
            const response = await api.get('/support');
            return response.data;
        }
    });
};

// Hook agregador para o componente Board público
export const useBoardData = () => {
    const { data: members, isLoading: loadingMembers } = useBoardMembers();
    const { data: support, isLoading: loadingSupport } = useSupportTeam();

    return {
        data: members && support ? { members, support } : null,
        isLoading: loadingMembers || loadingSupport
    };
};

// Mutação para Salvar Membro (Create/Update)
export const useSaveBoardMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: Partial<BoardMember>) => {
            if (data.id) {
                return api.put('/admin/board/members', data);
            }
            return api.post('/admin/board/members', data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['board-members'] });
        }
    });
};

// Mutação para Deletar Membro
export const useDeleteBoardMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            return api.delete(`/admin/board/members?id=${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['board-members'] });
        }
    });
};

// Mutação para Salvar Suporte (Create/Update)
export const useSaveSupportMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: Partial<SupportMember>) => {
            if (data.id) {
                return api.put('/admin/board/support', data);
            }
            return api.post('/admin/board/support', data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['support-team'] });
        }
    });
};

// Mutação para Deletar Suporte
export const useDeleteSupportMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            return api.delete(`/admin/board/support?id=${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['support-team'] });
        }
    });
};
